import {
  FormGroup,
  H3,
  HTMLSelect,
  NumericInput,
  Radio,
  RadioGroup,
} from '@blueprintjs/core';
import { Gender, RacialDenomination, WorkflowPerson } from '../../types';
import Unit from '../Unit';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { WavelightWorkflowState } from '../../views/workflow/types';
import { useCallback, useMemo } from 'react';
import {
  AGE,
  GENDER,
  HEIGHT,
  RACIAL_DENOMINATION,
  WEIGHT,
  loadPersonData,
  resetPersonData,
  setPersonData,
} from '../../views/workflow/reducer';
import {
  WorkflowFooter,
  WorkflowInnerContainer,
} from '../../views/workflow/pages/styles';
import { NextButton, PreviousButton } from '../FooterButtons';
import LoadDataButton from '../LoadDataButton';
import ResetDataButton from '../ResetDataButton';
import storage from '../../utils/storage';

const PersonData = () => {
  const dispatch = useAppDispatch();
  const { data }: WavelightWorkflowState = useAppSelector(
    (state) => state.workflow,
  );

  const onChange = useCallback(
    (partial: Partial<WorkflowPerson>) => {
      dispatch(
        setPersonData({
          ...data.person,
          ...partial,
        }),
      );
    },
    [dispatch, data.person],
  );

  const hasDataInLocalStorage =
    !!storage.get(HEIGHT) ||
    !!storage.get(WEIGHT) ||
    !!storage.get(AGE) ||
    !!storage.get(RACIAL_DENOMINATION) ||
    !!storage.get(GENDER);

  const hasDataInState = useMemo(
    () =>
      data.person.age > 0 ||
      data.person.gender !== Gender.UNDEFINED ||
      data.person.height > 0 ||
      data.person.racialDenomination !== RacialDenomination.UNDEFINED ||
      data.person.weight > 0,
    [data.person],
  );

  const handleReset = useCallback(() => {
    dispatch(resetPersonData());
  }, [dispatch]);

  const handleLoadData = useCallback(() => {
    dispatch(loadPersonData());
  }, [dispatch]);

  return (
    <>
      <WorkflowInnerContainer>
        <H3>2. Paciente</H3>
        {hasDataInLocalStorage && !hasDataInState && (
          <LoadDataButton handleLoadData={handleLoadData} />
        )}
        {hasDataInState && <ResetDataButton handleReset={handleReset} />}
        <FormGroup
          label={'Altura'}
          labelFor="height-input"
          labelInfo={'(obrigatório)'}
        >
          <NumericInput
            inputMode="numeric"
            selectAllOnFocus
            fill
            id="height-input"
            leftIcon="rect-height"
            value={data.person.height}
            onValueChange={(height) => onChange({ height })}
            min={0}
            max={300}
            clampValueOnBlur
            rightElement={<Unit>cm</Unit>}
            buttonPosition="none"
          />
        </FormGroup>
        <FormGroup
          label={'Peso'}
          labelFor="weight-input"
          labelInfo={'(obrigatório)'}
        >
          <NumericInput
            inputMode="numeric"
            selectAllOnFocus
            fill
            id="weight-input"
            leftIcon="horizontal-inbetween"
            value={data.person.weight}
            onValueChange={(weight) => onChange({ weight })}
            min={0}
            max={500}
            clampValueOnBlur
            rightElement={<Unit>kg</Unit>}
            buttonPosition="none"
          />
        </FormGroup>
        <FormGroup
          label={'Idade'}
          labelFor="age-input"
          labelInfo={'(obrigatório)'}
        >
          <NumericInput
            inputMode="numeric"
            selectAllOnFocus
            fill
            id="age-input"
            leftIcon="new-person"
            value={data.person.age}
            onValueChange={(age) => onChange({ age })}
            min={0}
            max={150}
            clampValueOnBlur
            rightElement={<Unit>anos</Unit>}
            buttonPosition="none"
          />
        </FormGroup>
        <FormGroup
          label={'Gênero'}
          labelFor="gender-input"
          labelInfo={'(obrigatório)'}
        >
          <RadioGroup
            onChange={(e) => {
              onChange({ gender: e.currentTarget.value as Gender });
            }}
            selectedValue={data.person.gender}
            inline
          >
            <Radio label="Masculino" value={Gender.MALE} />
            <Radio label="Feminino" value={Gender.FEMALE} />
          </RadioGroup>
        </FormGroup>
        <FormGroup
          label={'Denominação racial'}
          labelFor="racial-denomination-input"
          labelInfo={'(obrigatório)'}
        >
          <HTMLSelect
            fill
            options={[
              {
                label: 'Escolha um item...',
                value: RacialDenomination.UNDEFINED,
              },
              { label: 'Branco', value: RacialDenomination.WHITE },
              { label: 'Pardo', value: RacialDenomination.BROWN },
              { label: 'Preto', value: RacialDenomination.BLACK },
              { label: 'Oriental', value: RacialDenomination.ORIENTAL },
              { label: 'Indígena', value: RacialDenomination.INDIGENOUS },
            ]}
            value={data.person.racialDenomination}
            onChange={(e) => {
              onChange({
                racialDenomination: e.currentTarget.value as RacialDenomination,
              });
            }}
          />
        </FormGroup>
      </WorkflowInnerContainer>
      <WorkflowFooter>
        <PreviousButton />
        <NextButton />
      </WorkflowFooter>
    </>
  );
};

export default PersonData;
